import React, { useState } from 'react';
import { Button, Input, Form } from 'antd';
import { validateInputUrls } from '../utils';
import { useAddServiceableUrlsInClusterMutation } from './service';
import { useFetchWebsiteIndexEntriesQuery } from '../websiteIndexEntry/service';

const { TextArea } = Input;

const initialUrlsInputState = {
    individualUrls: '',
    validationError: '',
};

type Props = {
    websiteGuid: string;
    clusterGuid: string;
    maximumAllowedUrls: number;
    onFinish: () => void;
};

export default function AddNewServiceableUrls({ websiteGuid, clusterGuid, maximumAllowedUrls, onFinish }: Props) {
    const [urlsInput, setUrlsInput] = useState(initialUrlsInputState);

    const { data: websiteIndexEntries, isFetching: isLoadingWebsiteIndexEntries } = useFetchWebsiteIndexEntriesQuery();

    const [addServiceableUrlsInCluster, { isLoading: isAddingServiceableUrls }] = useAddServiceableUrlsInClusterMutation();

    const handleIndividualUrlsChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setUrlsInput({ individualUrls: event.currentTarget.value, validationError: '' });
    };

    const handleAddUrlsClick = async () => {
        const selectedWebsiteIndexEntry = websiteIndexEntries?.find((websiteIndexEntry) => websiteIndexEntry.guid === websiteGuid);

        if (selectedWebsiteIndexEntry) {
            const [inputUrls, inputUrlsValidationErrors] = validateInputUrls(urlsInput.individualUrls, selectedWebsiteIndexEntry.domain);

            if (inputUrlsValidationErrors.some((error) => error) || inputUrls.length === 0) {
                setUrlsInput({ ...urlsInput, validationError: `Please add valid URLs from domain: ${selectedWebsiteIndexEntry.domain}` });
                return;
            }

            if (inputUrls.length > maximumAllowedUrls) {
                setUrlsInput({ ...urlsInput, validationError: 'URLs exceeded limit' });
                return;
            }

            const uniqueUrls = inputUrls.filter((url, index) => inputUrls.indexOf(url) === index);

            try {
                await addServiceableUrlsInCluster({ w_guid: websiteGuid, cluster_guid: clusterGuid, urls: uniqueUrls }).unwrap();

                onFinish();
            } catch (error) {
                setUrlsInput({ ...urlsInput, validationError: 'Something went wrong! Please try again or contact support.' });
            }
        }
    };

    return (
        <Form
            className="deep-analysis-add-urls-form"
            layout="vertical"
        >
            <label
                className="form-label"
                htmlFor="textarea"
            >
                Add new URLs in this page group (maximum {maximumAllowedUrls} URLs, comma separated or one URL per line).
            </label>
            <Form.Item
                validateStatus={urlsInput.validationError ? 'error' : 'validating'}
                help={urlsInput.validationError ? urlsInput.validationError : ''}
            >
                <TextArea
                    disabled={isLoadingWebsiteIndexEntries}
                    placeholder="https://yourdomain.com/about"
                    value={urlsInput.individualUrls}
                    onChange={handleIndividualUrlsChange}
                    rows={6}
                    cols={100}
                    id="textarea"
                />
            </Form.Item>
            <Button
                className="add-urls-button"
                type="primary"
                size="small"
                onClick={handleAddUrlsClick}
                loading={isAddingServiceableUrls}
            >
                Add
            </Button>
        </Form>
    );
}
